import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Input,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Label,
  Spinner,
} from "@learn-me/ui";
import {
  ArrowLeft,
  Save,
  Trash2,
  Plus,
  ChevronDown,
  ChevronUp,
  GripVertical,
  AlertTriangle,
  CheckCircle,
  BookOpen,
  Lightbulb,
  FileText,
} from "lucide-react";
import toast from "react-hot-toast";
import { adminApi } from "@/features/admin/api/adminApi";

type Section = "CONCEPTUAL" | "APPLICATION" | "INTEGRATIVE_CASE";
type Difficulty = "EASY" | "MEDIUM" | "HARD";

interface QuestionForm {
  id?: string;
  text: string;
  explanation: string;
  points: number;
  section: Section;
  difficulty: Difficulty;
  tags: string[];
  options: { text: string; isCorrect: boolean }[];
  isOpen: boolean;
}

const SECTION_LABELS: Record<Section, string> = {
  CONCEPTUAL: "Conceptual",
  APPLICATION: "Aplicaci\u00f3n pr\u00e1ctica",
  INTEGRATIVE_CASE: "Caso integrador",
};

const SECTION_ICONS: Record<Section, typeof BookOpen> = {
  CONCEPTUAL: BookOpen,
  APPLICATION: Lightbulb,
  INTEGRATIVE_CASE: FileText,
};

const DIFFICULTY_LABELS: Record<Difficulty, string> = {
  EASY: "F\u00e1cil",
  MEDIUM: "Media",
  HARD: "Dif\u00edcil",
};

const DIFFICULTY_COLORS: Record<Difficulty, string> = {
  EASY: "bg-green-100 text-green-700",
  MEDIUM: "bg-yellow-100 text-yellow-700",
  HARD: "bg-red-100 text-red-700",
};

function createEmptyQuestion(section: Section): QuestionForm {
  return {
    text: "",
    explanation: "",
    points: 1,
    section,
    difficulty: "MEDIUM",
    tags: [],
    options: [
      { text: "", isCorrect: true },
      { text: "", isCorrect: false },
    ],
    isOpen: true,
  };
}

export default function FinalExamEditorPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Form state
  const [title, setTitle] = useState("Examen Final");
  const [description, setDescription] = useState("");
  const [passingScore, setPassingScore] = useState(70);
  const [maxAttempts, setMaxAttempts] = useState(2);
  const [timeLimit, setTimeLimit] = useState<number | "">("");
  const [shuffleQuestions, setShuffleQuestions] = useState(true);
  const [shuffleOptions, setShuffleOptions] = useState(true);
  const [requiredForCertificate, setRequiredForCertificate] = useState(true);

  // Section weights
  const [conceptualWeight, setConceptualWeight] = useState(25);
  const [applicationWeight, setApplicationWeight] = useState(35);
  const [integrativeCaseWeight, setIntegrativeCaseWeight] = useState(40);

  // Questions
  const [questions, setQuestions] = useState<QuestionForm[]>([]);
  const [activeTab, setActiveTab] = useState<Section>("CONCEPTUAL");

  // Fetch existing exam
  const { data: existingExam, isLoading } = useQuery({
    queryKey: ["final-exam-admin", courseId],
    queryFn: () => adminApi.getFinalExamByCourse(courseId!),
    enabled: !!courseId,
  });

  // Load existing data
  useEffect(() => {
    if (existingExam) {
      setTitle(existingExam.title);
      setDescription(existingExam.description || "");
      setPassingScore(existingExam.passingScore);
      setMaxAttempts(existingExam.maxAttempts);
      setTimeLimit(existingExam.timeLimit ?? "");
      setShuffleQuestions(existingExam.shuffleQuestions);
      setShuffleOptions(existingExam.shuffleOptions);
      setRequiredForCertificate(existingExam.requiredForCertificate);
      setConceptualWeight(existingExam.conceptualWeight);
      setApplicationWeight(existingExam.applicationWeight);
      setIntegrativeCaseWeight(existingExam.integrativeCaseWeight);
      setQuestions(
        existingExam.questions.map((q) => ({
          id: q.id,
          text: q.text,
          explanation: q.explanation || "",
          points: q.points,
          section: q.section,
          difficulty: q.difficulty,
          tags: q.tags,
          options: q.options.map((o) => ({
            text: o.text,
            isCorrect: o.isCorrect,
          })),
          isOpen: false,
        })),
      );
    }
  }, [existingExam]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof adminApi.createFinalExam>[0]) =>
      adminApi.createFinalExam(data),
    onSuccess: () => {
      toast.success("Examen final creado exitosamente");
      queryClient.invalidateQueries({
        queryKey: ["final-exam-admin", courseId],
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al crear el examen");
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Parameters<typeof adminApi.updateFinalExam>[1];
    }) => adminApi.updateFinalExam(id, data),
    onSuccess: () => {
      toast.success("Examen final actualizado");
      queryClient.invalidateQueries({
        queryKey: ["final-exam-admin", courseId],
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al actualizar");
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteFinalExam(id),
    onSuccess: () => {
      toast.success("Examen final eliminado");
      navigate(`/admin/courses/${courseId}`);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al eliminar");
    },
  });

  const weightsTotal =
    conceptualWeight + applicationWeight + integrativeCaseWeight;
  const weightsValid = weightsTotal === 100;

  const sectionQuestions = (section: Section) =>
    questions.filter((q) => q.section === section);

  const addQuestion = (section: Section) => {
    setQuestions([...questions, createEmptyQuestion(section)]);
  };

  const removeQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const updateQuestion = (index: number, updates: Partial<QuestionForm>) => {
    setQuestions(
      questions.map((q, i) => (i === index ? { ...q, ...updates } : q)),
    );
  };

  const addOption = (qIndex: number) => {
    const q = questions[qIndex];
    if (q.options.length >= 6) return;
    updateQuestion(qIndex, {
      options: [...q.options, { text: "", isCorrect: false }],
    });
  };

  const removeOption = (qIndex: number, oIndex: number) => {
    const q = questions[qIndex];
    if (q.options.length <= 2) return;
    updateQuestion(qIndex, {
      options: q.options.filter((_, i) => i !== oIndex),
    });
  };

  const updateOption = (
    qIndex: number,
    oIndex: number,
    updates: Partial<{ text: string; isCorrect: boolean }>,
  ) => {
    const q = questions[qIndex];
    updateQuestion(qIndex, {
      options: q.options.map((o, i) =>
        i === oIndex ? { ...o, ...updates } : o,
      ),
    });
  };

  const setCorrectOption = (qIndex: number, oIndex: number) => {
    const q = questions[qIndex];
    updateQuestion(qIndex, {
      options: q.options.map((o, i) => ({
        ...o,
        isCorrect: i === oIndex,
      })),
    });
  };

  const handleSave = () => {
    if (!weightsValid) {
      toast.error("Los pesos de las secciones deben sumar 100%");
      return;
    }

    // Validate questions
    for (const section of [
      "CONCEPTUAL",
      "APPLICATION",
      "INTEGRATIVE_CASE",
    ] as Section[]) {
      if (sectionQuestions(section).length === 0) {
        toast.error(
          `La secci\u00f3n "${SECTION_LABELS[section]}" debe tener al menos una pregunta`,
        );
        return;
      }
    }

    for (const q of questions) {
      if (!q.text.trim()) {
        toast.error("Todas las preguntas deben tener texto");
        return;
      }
      if (!q.options.some((o) => o.isCorrect)) {
        toast.error(
          "Cada pregunta debe tener al menos una opci\u00f3n correcta",
        );
        return;
      }
      if (q.options.some((o) => !o.text.trim())) {
        toast.error("Todas las opciones deben tener texto");
        return;
      }
    }

    if (existingExam) {
      // Update config only
      updateMutation.mutate({
        id: existingExam.id,
        data: {
          title,
          description: description || undefined,
          passingScore,
          maxAttempts,
          timeLimit: timeLimit || null,
          shuffleQuestions,
          shuffleOptions,
          requiredForCertificate,
          conceptualWeight,
          applicationWeight,
          integrativeCaseWeight,
        },
      });
    } else {
      // Create with questions
      createMutation.mutate({
        courseId: courseId!,
        title,
        description: description || undefined,
        passingScore,
        maxAttempts,
        timeLimit: timeLimit ? Number(timeLimit) : null,
        shuffleQuestions,
        shuffleOptions,
        requiredForCertificate,
        conceptualWeight,
        applicationWeight,
        integrativeCaseWeight,
        questions: questions.map((q) => ({
          text: q.text,
          explanation: q.explanation || undefined,
          points: q.points,
          section: q.section,
          difficulty: q.difficulty,
          tags: q.tags,
          options: q.options,
        })),
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to={`/admin/courses/${courseId}`}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-1 h-4 w-4" />
              Volver al curso
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">Examen Final</h1>
            <p className="text-sm text-muted-foreground">
              Evaluaci\u00f3n por competencias con 3 secciones ponderadas
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {existingExam && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                if (
                  confirm(
                    "\u00bfEliminar el examen final? Esta acci\u00f3n no se puede deshacer.",
                  )
                ) {
                  deleteMutation.mutate(existingExam.id);
                }
              }}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="mr-1 h-4 w-4" />
              Eliminar
            </Button>
          )}
          <Button onClick={handleSave} disabled={isSaving || !weightsValid}>
            <Save className="mr-1 h-4 w-4" />
            {isSaving ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </div>

      {/* Settings */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              Configuraci\u00f3n general
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>T\u00edtulo</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Examen Final del curso"
              />
            </div>
            <div>
              <Label>Descripci\u00f3n (opcional)</Label>
              <textarea
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Instrucciones para el estudiante..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Puntaje m\u00ednimo (%)</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={passingScore}
                  onChange={(e) => setPassingScore(Number(e.target.value))}
                />
              </div>
              <div>
                <Label>M\u00e1x. intentos</Label>
                <Input
                  type="number"
                  min={1}
                  max={5}
                  value={maxAttempts}
                  onChange={(e) => setMaxAttempts(Number(e.target.value))}
                />
              </div>
            </div>
            <div>
              <Label>
                L\u00edmite de tiempo (minutos, vac\u00edo = sin l\u00edmite)
              </Label>
              <Input
                type="number"
                min={1}
                value={timeLimit}
                onChange={(e) =>
                  setTimeLimit(e.target.value ? Number(e.target.value) : "")
                }
                placeholder="Sin l\u00edmite"
              />
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={shuffleQuestions}
                  onChange={(e) => setShuffleQuestions(e.target.checked)}
                  className="rounded"
                />
                Aleatorizar orden de preguntas
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={shuffleOptions}
                  onChange={(e) => setShuffleOptions(e.target.checked)}
                  className="rounded"
                />
                Aleatorizar orden de opciones
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={requiredForCertificate}
                  onChange={(e) => setRequiredForCertificate(e.target.checked)}
                  className="rounded"
                />
                Requerido para certificado
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Section Weights */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Pesos por secci\u00f3n</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Los pesos determinan cu\u00e1nto vale cada secci\u00f3n en la nota
              final. Deben sumar 100%.
            </p>

            <div className="space-y-3">
              <div>
                <Label className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-blue-500" />
                  Conceptual (20-30%)
                </Label>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min={20}
                    max={30}
                    value={conceptualWeight}
                    onChange={(e) =>
                      setConceptualWeight(Number(e.target.value))
                    }
                    className="flex-1"
                  />
                  <span className="w-12 text-right font-mono text-sm font-medium">
                    {conceptualWeight}%
                  </span>
                </div>
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-amber-500" />
                  Aplicaci\u00f3n pr\u00e1ctica (30-40%)
                </Label>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min={30}
                    max={40}
                    value={applicationWeight}
                    onChange={(e) =>
                      setApplicationWeight(Number(e.target.value))
                    }
                    className="flex-1"
                  />
                  <span className="w-12 text-right font-mono text-sm font-medium">
                    {applicationWeight}%
                  </span>
                </div>
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-purple-500" />
                  Caso integrador (30-40%)
                </Label>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min={30}
                    max={40}
                    value={integrativeCaseWeight}
                    onChange={(e) =>
                      setIntegrativeCaseWeight(Number(e.target.value))
                    }
                    className="flex-1"
                  />
                  <span className="w-12 text-right font-mono text-sm font-medium">
                    {integrativeCaseWeight}%
                  </span>
                </div>
              </div>
            </div>

            <div
              className={`flex items-center gap-2 rounded-lg p-3 text-sm ${
                weightsValid
                  ? "bg-green-50 text-green-700"
                  : "bg-red-50 text-red-700"
              }`}
            >
              {weightsValid ? (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Total: {weightsTotal}% - Correcto
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4" />
                  Total: {weightsTotal}% - Debe sumar 100%
                </>
              )}
            </div>

            {existingExam && (
              <div className="rounded-lg border p-3 text-sm text-muted-foreground">
                <p>
                  <strong>{existingExam._count.attempts}</strong> intentos
                  registrados
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Questions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Banco de preguntas ({questions.length} total)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as Section)}
          >
            <TabsList className="mb-4">
              {(
                ["CONCEPTUAL", "APPLICATION", "INTEGRATIVE_CASE"] as Section[]
              ).map((section) => {
                const Icon = SECTION_ICONS[section];
                const count = sectionQuestions(section).length;
                return (
                  <TabsTrigger key={section} value={section}>
                    <Icon className="mr-1.5 h-4 w-4" />
                    {SECTION_LABELS[section]} ({count})
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {(
              ["CONCEPTUAL", "APPLICATION", "INTEGRATIVE_CASE"] as Section[]
            ).map((section) => (
              <TabsContent key={section} value={section}>
                <div className="space-y-3">
                  {questions.map((q, globalIndex) => {
                    if (q.section !== section) return null;
                    return (
                      <QuestionEditor
                        key={globalIndex}
                        question={q}
                        index={globalIndex}
                        onUpdate={(updates) =>
                          updateQuestion(globalIndex, updates)
                        }
                        onRemove={() => removeQuestion(globalIndex)}
                        onAddOption={() => addOption(globalIndex)}
                        onRemoveOption={(oi) => removeOption(globalIndex, oi)}
                        onUpdateOption={(oi, updates) =>
                          updateOption(globalIndex, oi, updates)
                        }
                        onSetCorrect={(oi) => setCorrectOption(globalIndex, oi)}
                      />
                    );
                  })}

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => addQuestion(section)}
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Agregar pregunta a {SECTION_LABELS[section]}
                  </Button>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

function QuestionEditor({
  question,
  index,
  onUpdate,
  onRemove,
  onAddOption,
  onRemoveOption,
  onUpdateOption,
  onSetCorrect,
}: {
  question: QuestionForm;
  index: number;
  onUpdate: (updates: Partial<QuestionForm>) => void;
  onRemove: () => void;
  onAddOption: () => void;
  onRemoveOption: (oIndex: number) => void;
  onUpdateOption: (
    oIndex: number,
    updates: Partial<{ text: string; isCorrect: boolean }>,
  ) => void;
  onSetCorrect: (oIndex: number) => void;
}) {
  const sectionIndex =
    index -
    (question.section === "CONCEPTUAL"
      ? 0
      : question.section === "APPLICATION"
        ? 0
        : 0);

  return (
    <div className="rounded-lg border bg-card">
      {/* Header */}
      <div
        className="flex cursor-pointer items-center justify-between px-4 py-3"
        onClick={() => onUpdate({ isOpen: !question.isOpen })}
      >
        <div className="flex items-center gap-3">
          <GripVertical className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">
            {question.text || "Nueva pregunta"}
          </span>
          <Badge
            variant="secondary"
            className={DIFFICULTY_COLORS[question.difficulty]}
          >
            {DIFFICULTY_LABELS[question.difficulty]}
          </Badge>
          <Badge variant="outline">{question.points} pts</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              if (confirm("\u00bfEliminar esta pregunta?")) onRemove();
            }}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
          {question.isOpen ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </div>
      </div>

      {/* Body */}
      {question.isOpen && (
        <div className="space-y-4 border-t px-4 py-4">
          <div>
            <Label>Texto de la pregunta</Label>
            <textarea
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              rows={3}
              value={question.text}
              onChange={(e) => onUpdate({ text: e.target.value })}
              placeholder="Escribe la pregunta..."
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Puntos</Label>
              <Input
                type="number"
                min={1}
                value={question.points}
                onChange={(e) => onUpdate({ points: Number(e.target.value) })}
              />
            </div>
            <div>
              <Label>Dificultad</Label>
              <select
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={question.difficulty}
                onChange={(e) =>
                  onUpdate({ difficulty: e.target.value as Difficulty })
                }
              >
                <option value="EASY">F\u00e1cil</option>
                <option value="MEDIUM">Media</option>
                <option value="HARD">Dif\u00edcil</option>
              </select>
            </div>
            <div>
              <Label>Explicaci\u00f3n (opcional)</Label>
              <Input
                value={question.explanation}
                onChange={(e) => onUpdate({ explanation: e.target.value })}
                placeholder="Se muestra al revisar"
              />
            </div>
          </div>

          {/* Options */}
          <div>
            <Label className="mb-2 block">
              Opciones de respuesta ({question.options.length}/6)
            </Label>
            <div className="space-y-2">
              {question.options.map((opt, oIndex) => (
                <div key={oIndex} className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onSetCorrect(oIndex)}
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                      opt.isCorrect
                        ? "border-green-500 bg-green-500 text-white"
                        : "border-gray-300 hover:border-green-300"
                    }`}
                  >
                    {opt.isCorrect && <CheckCircle className="h-3 w-3" />}
                  </button>
                  <Input
                    value={opt.text}
                    onChange={(e) =>
                      onUpdateOption(oIndex, { text: e.target.value })
                    }
                    placeholder={`Opci\u00f3n ${oIndex + 1}`}
                    className="flex-1"
                  />
                  {question.options.length > 2 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemoveOption(oIndex)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            {question.options.length < 6 && (
              <Button
                variant="ghost"
                size="sm"
                className="mt-2"
                onClick={onAddOption}
              >
                <Plus className="mr-1 h-3 w-3" />
                Agregar opci\u00f3n
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
